import { encodeGameText } from './charset';

// Save "novo jogo": a flash de 128 KiB de um jogo salvo logo depois da abertura do
// Prof. Oak (no quarto), gerada uma vez por nós. Para cada jogador novo trocamos nome,
// gênero, rival, ID de treinador e velocidade do texto, e corrigimos os checksums.
// Ao dar CONTINUAR, o próprio jogo carrega tudo isso — inclusive o sprite certo.
//
// Formato (include/save.h e src/save.c do pret/pokefirered): 2 slots de 14 setores de
// 4 KiB; cada setor tem até 3968 bytes de dados e um rodapé com id da seção, checksum,
// assinatura 0x08012025 e um contador (o slot com o maior contador é o atual).

const SECTOR_SIZE = 0x1000;
const SECTORS_PER_SLOT = 14;
const FOOTER_ID = 0xff4;
const FOOTER_CHECKSUM = 0xff6;
const FOOTER_SIGNATURE = 0xff8;
const FOOTER_COUNTER = 0xffc;
const SIGNATURE = 0x08012025;
const CHUNK = 0xf80;

const SAVEBLOCK2_SIZE = 0xf24;
const SAVEBLOCK1_SIZE = 0x3d68;
const STORAGE_SIZE = 0x83d0;

/** Quantos bytes de cada seção entram no checksum. */
function sectionSize(id: number): number {
  if (id === 0) return SAVEBLOCK2_SIZE;
  if (id >= 1 && id <= 4) return Math.min(CHUNK, SAVEBLOCK1_SIZE - (id - 1) * CHUNK);
  return Math.min(CHUNK, STORAGE_SIZE - (id - 5) * CHUNK);
}

// SaveBlock2 (seção 0)
const SB2_NAME = 0x00;
const SB2_GENDER = 0x08;
const SB2_TRAINER_ID = 0x0a;
const SB2_OPTIONS = 0x14; // bits 0-2: velocidade do texto
const TEXT_SPEED_FAST = 2;
// SaveBlock1: rivalName em 0x3A4C → seção 1 + 3 = 4, offset 0x3A4C - 3 * 0xF80
const RIVAL_SECTION = 4;
const RIVAL_OFFSET = 0x3a4c - 3 * CHUNK;
// SaveBlock1.objectEvents (0x06A0, 16 × 0x24 bytes, inteiro na seção 1). Ao CONTINUAR, o
// jogo recria o jogador a partir daqui e deduz o gênero pelo graphicsId (+0x05).
const OBJECTS_SECTION = 1;
const OBJECTS_OFFSET = 0x06a0;
const OBJECT_SIZE = 0x24;
const OBJECT_COUNT = 16;
const OBJECT_GRAPHICS_ID = 0x05;
const PLAYER_GRAPHICS = [0, 7]; // OBJ_EVENT_GFX_RED_NORMAL, OBJ_EVENT_GFX_GREEN_NORMAL
// SaveBlock1.vars (0x1000, u16 por variável a partir de 0x4000) — na seção 2 (começa em 0xF80).
// Inicial escolhido no app: VAR_STARTER_MON e a cena do quarto = 2, que dispara o script
// Dynasty_EventScript_GiveStarter da nossa ROM (personalizacoes/aplicar.py na decompilação).
const VARS_SECTION = 2;
const varOffset = (id: number) => 0x1000 + (id - 0x4000) * 2 - CHUNK;
const VAR_STARTER_MON = 0x4031;
const VAR_BEDROOM_SCENE = 0x4056; // VAR_MAP_SCENE_PALLET_TOWN_PLAYERS_HOUSE_2F
const BEDROOM_GIVE_STARTER = 2;
const NAME_SIZE = 8; // 7 letras + terminador

export interface NewTrainer {
  name: string;
  rival: string;
  gender: 0 | 1;
  /** ID de treinador (32 bits; o jogo mostra os 16 de baixo). */
  trainerId: number;
  /** VAR_STARTER_MON (0 BULBASAUR, 1 SQUIRTLE, 2 CHARMANDER); null = sem inicial. */
  starter: 0 | 1 | 2 | null;
}

function checksum(view: DataView, start: number, size: number): number {
  let sum = 0;
  for (let i = 0; i < size; i += 4) sum = (sum + view.getUint32(start + i, true)) >>> 0;
  return ((sum >>> 16) + sum) & 0xffff;
}

/** Acha o slot atual e devolve, para cada id de seção, o offset do setor na flash. */
function currentSlot(view: DataView): Map<number, number> {
  let best: { counter: number; sectors: Map<number, number> } | null = null;
  for (let slot = 0; slot < 2; slot++) {
    const sectors = new Map<number, number>();
    let counter = -1;
    for (let i = 0; i < SECTORS_PER_SLOT; i++) {
      const base = (slot * SECTORS_PER_SLOT + i) * SECTOR_SIZE;
      if (view.getUint32(base + FOOTER_SIGNATURE, true) !== SIGNATURE) continue;
      const id = view.getUint16(base + FOOTER_ID, true);
      if (id >= SECTORS_PER_SLOT || checksum(view, base, sectionSize(id)) !== view.getUint16(base + FOOTER_CHECKSUM, true)) continue;
      sectors.set(id, base);
      counter = view.getUint32(base + FOOTER_COUNTER, true);
    }
    if (sectors.size === SECTORS_PER_SLOT && (!best || counter > best.counter)) best = { counter, sectors };
  }
  if (!best) throw new Error('save modelo inválido');
  return best.sectors;
}

export function buildNewGameSave(template: Uint8Array, trainer: NewTrainer): Uint8Array {
  const save = template.slice();
  const view = new DataView(save.buffer);
  const sectors = currentSlot(view);
  const sb2 = sectors.get(0)!;
  const rivalSector = sectors.get(RIVAL_SECTION)!;
  const objectsSector = sectors.get(OBJECTS_SECTION)!;
  const varsSector = sectors.get(VARS_SECTION)!;

  save.set(encodeGameText(trainer.name, NAME_SIZE), sb2 + SB2_NAME);
  save[sb2 + SB2_GENDER] = trainer.gender;
  view.setUint32(sb2 + SB2_TRAINER_ID, trainer.trainerId >>> 0, true);
  const options = view.getUint16(sb2 + SB2_OPTIONS, true);
  view.setUint16(sb2 + SB2_OPTIONS, (options & ~0b111) | TEXT_SPEED_FAST, true);
  save.set(encodeGameText(trainer.rival, NAME_SIZE), rivalSector + RIVAL_OFFSET);

  let playerFound = false;
  for (let i = 0; i < OBJECT_COUNT; i++) {
    const object = objectsSector + OBJECTS_OFFSET + i * OBJECT_SIZE;
    const flags = view.getUint32(object, true);
    const active = (flags & 1) !== 0;
    const isPlayer = ((flags >>> 16) & 1) !== 0;
    if (active && isPlayer) {
      save[object + OBJECT_GRAPHICS_ID] = PLAYER_GRAPHICS[trainer.gender];
      playerFound = true;
    }
  }
  if (!playerFound) throw new Error('jogador não encontrado no save modelo');

  if (trainer.starter !== null) {
    view.setUint16(varsSector + varOffset(VAR_STARTER_MON), trainer.starter, true);
    view.setUint16(varsSector + varOffset(VAR_BEDROOM_SCENE), BEDROOM_GIVE_STARTER, true);
  }

  for (const [id, base] of [
    [0, sb2],
    [OBJECTS_SECTION, objectsSector],
    [VARS_SECTION, varsSector],
    [RIVAL_SECTION, rivalSector],
  ]) {
    view.setUint16(base + FOOTER_CHECKSUM, checksum(view, base, sectionSize(id)), true);
  }
  return save;
}
